import { useState } from "react";
import {
  TestTube,
  PaperPlaneTilt,
  CheckCircle,
  Warning,
  WhatsappLogo,
  EnvelopeSimple,
  Lightning,
} from "@phosphor-icons/react";
import { postSimulate } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  { name: "Maria G.", body: "Hi! What are your opening hours today?" },
  { name: "Tom P.", body: "Do you ship internationally? How long to Germany?" },
  { name: "Sarah L.", body: "I want a refund on order #4521 — it arrived broken." },
  { name: "Kevin M.", body: "Do you serve oat milk?" },
];

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "simulator", label: "Test", icon: Lightning },
];

export default function Simulator() {
  const [channel, setChannel] = useState("simulator");
  const [senderName, setSenderName] = useState("Test Customer");
  const [senderId, setSenderId] = useState("customer@example.com");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!body.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await postSimulate({
        channel,
        sender_name: senderName,
        sender_id: senderId,
        subject,
        body: body.trim(),
      });
      setResult(res);
      if (res.status === "escalated") {
        toast.warning("Bot escalated this message to you.");
      } else {
        toast.success("Bot generated an auto-reply.");
      }
    } catch (e) {
      toast.error("Failed to simulate.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="simulator-page">
      <div className="mb-8">
        <div className="overline">Simulator</div>
        <h1 className="font-semibold text-3xl sm:text-4xl tracking-tight mt-2">
          Teste wie dein Bot antwortet
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Schick eine simulierte Kunden-Nachricht. Der Bot antwortet auf Basis
          deiner FAQs und Business-Daten, oder eskaliert an dich wenn er unsicher ist.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input */}
        <div className="card-soft p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-secondary/60 flex items-center justify-center">
              <TestTube size={16} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="overline">Eingehende Nachricht</div>
          </div>

          <div className="mb-4">
            <div className="overline mb-2">Kanal</div>
            <div className="flex rounded-xl overflow-hidden border border-border-soft">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.key;
                return (
                  <button
                    key={c.key}
                    data-testid={`sim-channel-${c.key}`}
                    onClick={() => setChannel(c.key)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium uppercase tracking-wider border-r border-border-soft last:border-r-0 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-white hover:bg-secondary"
                    }`}
                  >
                    <Icon size={14} weight="bold" /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="overline block mb-2">Absender-Name</label>
              <input
                data-testid="sim-name"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
              />
            </div>
            <div>
              <label className="overline block mb-2">
                {channel === "whatsapp" ? "Telefon" : "Email / ID"}
              </label>
              <input
                data-testid="sim-id"
                value={senderId}
                onChange={(e) => setSenderId(e.target.value)}
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
              />
            </div>
          </div>

          {channel === "email" && (
            <div className="mb-4">
              <label className="overline block mb-2">Betreff</label>
              <input
                data-testid="sim-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Frage zu meiner Bestellung"
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="overline block mb-2">Nachricht</label>
            <textarea
              data-testid="sim-body"
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Tippe hier die Kunden-Nachricht…"
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
            />
          </div>

          <div className="mb-4">
            <div className="overline mb-2">Schnellbeispiele</div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  data-testid={`sim-example-${i}`}
                  className="border border-border-soft rounded-full px-3 py-1.5 text-xs hover:bg-secondary transition-colors"
                  onClick={() => {
                    setSenderName(ex.name);
                    setBody(ex.body);
                  }}
                >
                  {ex.body.slice(0, 40)}…
                </button>
              ))}
            </div>
          </div>

          <button
            className="btn-invert w-full justify-center disabled:opacity-50"
            disabled={loading || !body.trim()}
            onClick={run}
            data-testid="sim-run-btn"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            {loading ? "Denke nach…" : "An Bot senden"}
          </button>
        </div>

        {/* Result */}
        <div className="card-soft p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Lightning size={16} weight="duotone" className="text-accent" />
            </div>
            <div className="overline">Bot-Entscheidung</div>
          </div>
          {!result && (
            <div className="text-sm text-muted-foreground leading-relaxed">
              Schicke eine Simulation um zu sehen wie der Bot reagiert. Sichere
              Antworten gehen automatisch raus; unklare oder komplexe Nachrichten
              werden in die Inbox eskaliert.
            </div>
          )}
          {result && result.status === "escalated" && (
            <div data-testid="sim-result-escalated">
              <div className="flex items-center gap-2 mb-3">
                <Warning size={20} weight="fill" className="text-destructive" />
                <span className="bg-destructive text-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider">
                  Eskaliert
                </span>
              </div>
              <div className="font-display font-bold text-xl tracking-tight mb-2">
                Der Bot hat das für dich markiert.
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Der Bot war sich unsicher. Die Nachricht liegt jetzt mit dem
                Eskaliert-Badge in deiner Inbox und du bekommst eine
                Benachrichtigung.
              </p>
            </div>
          )}
          {result && result.ai_reply && (
            <div data-testid="sim-result-answer">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={20} weight="fill" className="text-accent" />
                <span className="bg-accent text-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider">
                  Auto-Antwort
                </span>
              </div>
              <div className="border border-border-soft rounded-xl p-4 bg-secondary/30 whitespace-pre-wrap leading-relaxed text-sm">
                {result.ai_reply}
              </div>
              {result.delivery_error && (
                <div className="mt-3 text-xs text-destructive">
                  Hinweis: {result.delivery_error} (Antwort generiert, nicht ausgeliefert)
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
